/* Program Name: Header.js
 **  Description: component for the top app bar for the application
 **
 **
 ** Project    Date        Who  Comments
 ** ---------  ----------  ---  ---------------------------------------------------
 ** IS0200241  2021-03-17  STT  Initial program
 ** IS200324   2023-06-09  tjs  Fixed NavClick error
 ** SD6391     2024-09-03  sgs  SD-8077 - Removed TabPanel functionality. Account,
 **                             Addresses, and CreditCards have become seperate pages
 ** SD6391     2025-01-20  sgs  SD-9219 - Shopping - Increase Shopping Cart field
 ** SD9567     2026-07-29  sgs  ITSM-970 - Apply UI assets (colors, logos, fonts, etc) 
 **                             to FHS site
 *********************************************************************************/

/**React imports */
import React, { useEffect, useRef, useState } from 'react'
import { keyframes } from '@emotion/react'
import { useCallback } from 'react'
import { useTheme } from '@emotion/react'

/**react-router imports */ //DOCS: https://reactrouter.com/
import { useHistory, useLocation } from 'react-router-dom'

/**React-Router imports */
import { NavLink } from 'react-router-dom'

/**material-ui imports */ //DOCS: https://material-ui.com/components/
import {
  Toolbar,
  Typography,
  Badge,
  Tooltip,
  AppBar,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material'

import {
  ShoppingCart,
  //HelpOutline,
  ManageAccounts,
  Logout,
  Payment,
  LocationOn,
  //PersonSearch,
  //ShoppingCartCheckout,
} from '@mui/icons-material'

/** Custom Component imports */
import { FlexOne } from '../CustomDivs'
import '../.././../index.css'

/**Context imports */
import { useSession } from '../../../Context/SessionContext'
import { useCart } from '../../../Context/CartContext'

/**Style imports */
import SupportDialog from './SupportDialog'
import { getOrderSummary } from '../../../API/generalAPI'
import { useAlert } from '../../../Context/AlertContext'

/** API imports */
import { getMeta } from '../../../API/generalAPI'
import { getMyAccount } from '../../Account/accountAPI'
import { makeTree } from '../../../API/generalFunctions'

/**General Funtion imports */
import { GetIcon } from '../SideBar/MenuIcons'
import UserLookUp from './UserLookUp'
import UserProxy from './UserProxy'
import { DEV_GREEN, QA_YELLOW, subdomain } from '../ezpopURL'

/**Header *********************************************************************/
const Header = () => {
  const theme = useTheme()

  /****************************************************************************/
  /** Header Styles */

  //determines header color based on development level
  const headerColor = subdomain.includes('-dev')
    ? DEV_GREEN
    : subdomain.includes('-qa')
    ? QA_YELLOW
    : theme.palette.header
    ? theme.palette.header.main
    : theme.palette.primary.main

  //animation for the color changing on dev and qa
  const swap = keyframes`
    0% { background-position: left; }
    100% { background-position: right; }
  `
  const styles = {
    //styles for the header
    header: {
      zIndex: 5,
      height: 48,
      animation: `${swap} 4s infinite alternate`,
      background:
        subdomain.includes('-dev') || subdomain.includes('-qa')
          ? `linear-gradient(to right, ${theme.palette.primary.main}, ${headerColor})`
          : null,
      backgroundColor: theme.palette.header
        ? theme.palette.header.main
        : theme.palette.primary.main,
      backgroundSize: '300%',
      boxShadow: 'none',
      color: theme.palette.getContrastText(
        theme.palette.header
          ? theme.palette.header.main
          : theme.palette.primary.main
      ),
    },

    toolbar: {
      paddingLeft: 32,
      paddingRight: 32,
    },

    //styles for the bade on the header
    badge: {
      marginRight: '32px',
    },

    //styles for header icons
    icon: {
      cursor: 'pointer',
      fontSize: 28,
      marginLeft: '6px',
      marginRight: '6px',
    },

    //styles from the divider div
    flexDiv: {
      flex: 1,
    },

    signOut: {
      cursor: 'pointer',
      fontSize: 20,
    },

    drawerDiv: {
      height: '85vh',
      width: 250,
      margin: 8,
    },

    drawerRow: {
      width: 250,
    },
  }

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    }
  }

  /****************************************************************************/
  /**Local Variables */

  /**logout function from the SessionContext */
  const { handleLogout } = useSession()
  //content of the current cart
  const { currentCartCnt } = useCart()
  //Context end point to access global data about the cart
  const cart = useRef(useCart())
  //Context end point to access global alert system
  const alert = useRef(useAlert())
  //holds the BrowserRouter history for the application router
  //hook from react-router
  const history = useHistory()
  //Context end point to access react router data
  const location = useLocation()
  //Contains the state of the currently logged in session
  const [currentSession, setCurrentSession] = useState({ dispName: 'Account' })
  //determines wether or not the support info modal is open or closed
  const [supportModal, setSupportModal] = useState(false)
  //Boolean which determines if the the userlookup modal is open or not
  const [userModal, setUserModal] = useState(false)
  //status of drawer, true = open, false = close
  const [drawer, setDrawer] = useState(false)
  //contain dom information to anchor the dropdown menus to the buttons position
  const [anchorEl, setAnchorEl] = useState(null)
  //Determines which dropdown menu is open
  const [menu, setMenu] = useState([])
  //Contains the value of the currently clicked on tab
  const [value, setValue] = useState(0)
  //Boolean to determine if the acount menu is open or closed
  const [open, setOpen] = useState(null)
  //Contains data of currently proxied user if applicable
  const [proxyUser, setProxyUser] = useState(null)
  //Contains data of the current users account
  const [myAccount, setMyAccount] = useState({})

  /****************************************************************************/
  /**Local Functions */

  /******************************************************************************/
  /**Handle Click*/
  /******************************************************************************/
  // Handles the click event to open the account dropdown menu
  //sets the anchor to the position of the button
  const handleClick = (event) => {
    setOpen('basic-menu')
    setAnchorEl(event.currentTarget)
  }

  /******************************************************************************/
  /**Handle Close*/
  /******************************************************************************/
  // Handles the closing event of the account dropdown menu
  //removes the anchor
  const handleClose = () => {
    setOpen(null)
    setAnchorEl(null)
  }

  /******************************************************************************/
  /**Close Modal*/
  /******************************************************************************/
  // Handles the closing event of the support modal
  const closeModal = () => {
    setSupportModal(false)
  }

  /******************************************************************************/
  /**Go To Cart*/
  /******************************************************************************/
  // Handles the sending user to cart screen
  const goToCart = () => {
    setDrawer(false)
    history.push('/cart')
  }

  /******************************************************************************/
  /**Handle Nav Click*/
  /******************************************************************************/
  /** handles the selection of a navigation menu item that will send the user to
   * the appropriate screen of the application
   *  * @param {Object} menuItem - the data of the menuItem that was clicked
   */
  const handleNavClick = (menuItem, event) => {
    setValue(menuItem.id)
    if (menuItem.id === 0) {
      history.push('/')
    }
    if (typeof menuItem.children == 'undefined') {
      history.push(menuItem.route)
    } else {
      setOpen(menuItem.dispText)
      setAnchorEl(event.currentTarget)
    }
  }

  /******************************************************************************/
  /**Handle Proxy Lookup*/
  /******************************************************************************/
  // Handles opening the user lookup modal from the proxied toolbar
  const handleProxyLookup = () => {
    setUserModal(true)
  }

  /******************************************************************************/
  /**Handle Copy Message*/
  /******************************************************************************/
  // Handles sending the current user a message when they copy a user's information
  //to the clipboard
  const handleCopyMessage = () => {
    alert.current.successMessage('Copied User Information to clipboard')
  }

  /******************************************************************************/
  /**Get Meta Data*/
  /******************************************************************************/
  // Handles calling the API to get all nessacary meta data for the app bar
  const getMetaData = useCallback(() => {
    getMeta().then((response) => {
      if (response.data.meta[0].err) {
        alert.current.postAjaxAlert(response.data.meta[0])
      } else {
        if (response.data.ttMainMenu) {
          var tree = makeTree(response.data.ttMainMenu, 'parentId')
          var abmenu = []
          tree.forEach((menu) => {
            if (menu.children.length > 0) {
              menu.children.forEach((subMenu) => {
                abmenu.push(subMenu)
              })
            }
          })
          var route = response.data.ttMainMenu.find(
            (item) => item.route === location.pathname
          )
          if (route) {
            if (route.parentId) {
              setValue(route.parentId)
            } else {
              setValue(route.id)
            }
          }
          setMenu(tree)
          setCurrentSession(response.data.ttMetaUser[0])
        } else {
          setMenu([])
        }
      }
      if (response.data.ttProxyUser) {
        setProxyUser(response.data.ttProxyUser[0])
      } else {
        setProxyUser(null)
      }
    })
  }, [location])

  /******************************************************************************/
  /**Get Account Info*/
  /******************************************************************************/
  // Handles calling the API to get all account info for logged in user
  const getAccountInfo = useCallback(() => {
    getMyAccount()
      .then((response) => {
        alert.current.postAjaxAlert(response.data.meta[0], 'rdr')
        if (!response.data.meta[0].err) {
          if (response.data.ttMyAccount) {
            setMyAccount(response.data.ttMyAccount[0])
          }
        }
      })
      .catch((error) => {
        alert.current.errorMessage(error.message)
      })
  }, [])

  /******************************************************************************/
  /**useEffect */

  useEffect(() => {
    cart.current.getCartCnt()
  }, [])

  /**gets the menu data from an AJAX call based on currentSession */
  useEffect(() => {
    getMetaData()
  }, [])

  useEffect(() => {
    if (drawer) {
      getOrderSummary()
        .then((response) => {
          if (response.data.meta[0].err) {
            alert.current.postAjaxAlert(response.data.meta[0])
          } else {
          }
        })
        .catch((error) => {
          alert.current.errorMessage(error.message)
        })
        .then(() => {})
    }
  }, [drawer])

  useEffect(() => {
    getAccountInfo()
  }, [getAccountInfo])

  /****************************************************************************/
  /**Rendering */

  return (
    <>
      <AppBar position="sticky" sx={styles.header}>
        <Toolbar variant="dense" sx={styles.toolbar}>
          <img
            style={{ height: '40px', marginRight: '24px', cursor: 'pointer' }}
            onClick={() => {
              history.push('/')
            }}
            src={`/media/${theme.logo ? theme.logo.image : ''}`}
            alt={`${theme.logo ? theme.logo.image : ''} logo`}
          />
          <Typography start="edge" variant="h5" marginRight='16px' lineHeight='0.9em'>
            {currentSession.siteTitle}
          </Typography>
          <Tabs
            value={value}
            textColor={'inherit'}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{
              style: {
                backgroundColor: '#fff',
              },
            }}
          >
            <Tab
              sx={{
                color: theme.palette.getContrastText(
                  theme.palette.header
                    ? theme.palette.header.main
                    : theme.palette.primary.main
                ),
              }}
              key={0}
              value={0}
              label={'Home'}
              onClick={() => {
                handleNavClick({ id: 0 })
              }}
              {...a11yProps(0)}
            />
            {menu.map((menuItem) => {
              return (
                <Tab
                  sx={{
                    color: theme.palette.getContrastText(
                      theme.palette.header
                        ? theme.palette.header.main
                        : theme.palette.primary.main
                    ),
                  }}
                  key={menuItem.id}
                  value={menuItem.id}
                  label={menuItem.dispText}
                  component={menuItem.children.length > 0 ? null : NavLink}
                  to={
                    menuItem.children.length > 0
                      ? null
                      : menuItem.route
                      ? menuItem.route
                      : ''
                  }
                  onClick={(e) => {
                    handleNavClick(menuItem, e)
                  }}
                  {...a11yProps(menuItem.id)}
                  aria-controls={open ? menuItem.dispText : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                />
              )
            })}
          </Tabs>
          <FlexOne />
          {currentSession.userLookup && (
            <Button
              color={
                theme.palette.header
                  ? theme.palette.header.highlight
                  : 'primary'
              }
              onClick={() => {
                setUserModal(true)
              }}
            >
              User Lookup
            </Button>
          )}
          <Button
            color={
              theme.palette.header ? theme.palette.header.highlight : 'primary'
            }
            onClick={() => {
              setSupportModal(true)
            }}
          >
            Contact Us/FAQ
          </Button>
          <Tooltip disableInteractive title="Shopping Cart">
            <Badge
              badgeContent={currentCartCnt}
              max={999}
              color={
                theme.palette.header
                  ? theme.palette.header.highlight
                  : 'primary'
              }
            >
              <ShoppingCart sx={styles.icon} onClick={goToCart} />
            </Badge>
          </Tooltip>
          <Button
            id="basic-button"
            color={
              theme.palette.header
                ? theme.palette.header.highlight
                : 'secondary'
            }
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
          >
            {currentSession.dispName}
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open === 'basic-menu'}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <MenuItem
              onClick={() => {
                handleClose()
                history.push('/account')
              }}
            >
              <ListItemIcon>
                <ManageAccounts fontSize="small" />
              </ListItemIcon>
              <ListItemText>My Account</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose()
                history.push('/maint/addresses')
              }}
            >
              <ListItemIcon>
                <LocationOn fontSize="small" />
              </ListItemIcon>
              <ListItemText>Addresses</ListItemText>
            </MenuItem>
            {myAccount.cardOpt && (
              <MenuItem
                onClick={() => {
                  handleClose()
                  history.push('/maint/cards')
                }}
              >
                <ListItemIcon>
                  <Payment fontSize="small" />
                </ListItemIcon>
                <ListItemText>Credit Cards</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleClose()
                history.push('/mycarts')
              }}
            >
              <ListItemIcon>
                <ShoppingCart fontSize="small" />
              </ListItemIcon>
              <ListItemText>My Carts</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Log Out</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
        <SupportDialog open={supportModal} close={closeModal} />
        <UserLookUp
          open={userModal}
          close={() => {
            setUserModal(false)
          }}
          getMetaData={getMetaData}
        />
      </AppBar>
      <div>
        {menu.map((menuItem) => {
          if (menuItem.children.length > 0) {
            return (
              <Menu
                key={menuItem.id + '-panel'}
                id={menuItem.dispText}
                anchorEl={anchorEl}
                open={open === menuItem.dispText}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': menuItem.dispText,
                }}
              >
                {menuItem.children.map((child) => {
                  return (
                    <MenuItem
                      style={{ height: '48px' }}
                      component={NavLink}
                      to={child.route}
                      key={child.id}
                      onClick={() => {
                        handleClose()
                      }}
                    >
                      <ListItemIcon>
                        <GetIcon iconName={child.dispIcon} />
                      </ListItemIcon>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '16px' }}>
                          {child.dispText}
                        </div>
                      </div>
                    </MenuItem>
                  )
                })}
              </Menu>
            )
          } else {
            return null
          }
        })}
      </div>
      {proxyUser && (
        <UserProxy
          proxyUser={proxyUser}
          getMetaData={getMetaData}
          getLookUp={handleProxyLookup}
          copyMessage={handleCopyMessage}
        />
      )}
    </>
  )
}

export default Header
